import { createContext } from "react";
import { LOGO_ANIMATION_MS, type PartAnimation } from "./timeline";

/**
 * When a logo is written (ADR 0016).
 *
 * `own`: from the start, the moment it comes on the screen. The opening of the till.
 *
 * `page`: with the page. The logo at the top is written once per load of the page: every such logo
 * follows one clock, which starts with the first of them. One that comes on the screen later shows the
 * same moment, or is the mark at once when the writing is over: the next screen of the Tafo app has the
 * head again and does not write the logo again.
 */
export type LogoClock = "own" | "page";

/**
 * True while something that writes the logo itself lies over the page (the opening of the till): a logo
 * on the clock of the page waits behind it, and is written when it leaves.
 */
export const LogoWaits = createContext(false);

/** When the writing at the top began, on the timeline of the page. */
let began: number | null = null;

/** Whether the logo is animated here: the browser can, and nobody asked the system for less motion. */
export function logoAnimates(): boolean {
  if (!("animate" in Element.prototype) || !("timeline" in document)) return false;
  return !(typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
}

/**
 * Starts the animations of the parts found under `root` (`data-part`). They all get the same start on
 * the timeline of the page, so every part of a logo, and every logo on the clock of the page, shows the
 * same moment to the frame. Nothing when the logo is not animated here, or was written already.
 */
export function playLogo(root: Element, parts: readonly PartAnimation[], clock: LogoClock): Animation[] {
  if (!logoAnimates()) return [];
  const now = document.timeline.currentTime;
  if (typeof now !== "number") return []; // no time: the document is not the active one of its window
  let start = now;
  if (clock === "page") {
    began ??= now;
    if (now - began >= LOGO_ANIMATION_MS) return [];
    start = began;
  }
  const animations: Animation[] = [];
  try {
    for (const { part, keyframes, timing } of parts) {
      const element = root.querySelector(`[data-part="${part}"]`);
      if (element === null) continue;
      const animation = element.animate(keyframes, timing);
      animations.push(animation);
      animation.startTime = start;
    }
  } catch {
    // a browser that refuses a part of it shows the mark: the logo never stands in the way of the till
    for (const animation of animations) animation.cancel();
    return [];
  }
  return animations;
}

/** For the tests: the next logo at the top is the first on its page again. */
export function forgetLogoClock(): void {
  began = null;
}
